import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CopyToClipboard } from '@delmonicos/shared-design-system-react/src/components/copy-to-clipboard/copy-to-clipboard';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { TransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/transaction.model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { mapTypeToGenericConnectorType } from '@app/feature/charge-point/helper/connector-type.helper';

interface TypeCardComponentProps {
  transaction: TransactionModel;
}

export const TypeCardComponent = ({
  transaction,
}: TypeCardComponentProps): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const type = useMemo(
    () =>
      transaction.physicalSocket?.standard
        ? mapTypeToGenericConnectorType(transaction.physicalSocket?.standard)
        : null,
    [transaction],
  );

  const handleGoToLocation = () => {
    if (!transaction.location) {
      return;
    }

    navigate(
      generatePath(APP_PATH.LOCATION.DETAILS, { id: transaction.location?.id }),
    );
  };

  const handleGoToChargePoint = () => {
    if (!transaction.chargePoint) {
      return;
    }

    navigate(
      generatePath(APP_PATH.CHARGE_POINT.DETAILS, {
        id: transaction.chargePoint?.id,
      }),
    );
  };

  const handleGoToTariff = () => {
    if (!transaction.tariffId) {
      return;
    }

    navigate(
      generatePath(APP_PATH.TARIFF.DETAILS, { id: transaction.tariffId }),
    );
  };

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <Stack
          gap={GapEnum.M}
          justifyContent="space-between"
          alignItems="center">
          <AccentTitle
            icon={type?.icon ?? 'no'}
            text={
              type?.name
                ? t(
                    `delmonicos-shared-sdk.ocpi-protocol-enums.connector-type-alt.${type.name}`,
                  )
                : '-'
            }
            as={HeadingEnum.H2}
          />
          <Stack gap={GapEnum.S}>
            <Text color={TextColor.Secondary} fontSize={TextFontSize.Medium}>
              {t('$.transaction-details.connector-id')}
            </Text>
            <CopyToClipboard textToCopy={transaction.physicalSocket?.id ?? ''}>
              <Text
                color={TextColor.Secondary}
                fontSize={TextFontSize.Medium}
                truncate>
                {transaction.physicalSocket?.id ?? '-'}
              </Text>
            </CopyToClipboard>
          </Stack>
        </Stack>
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Text bold>
            <Icon name="map-01" />
            {t('delmonicos-shared-sdk.outputs-transaction.location')}
          </Text>
          <Link onClick={handleGoToLocation} disabled={!transaction.location}>
            {transaction.location?.name ?? '-'}
          </Link>
        </Stack>
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Text bold>
            <Icon name="electric-terminal" />
            {t('delmonicos-shared-sdk.outputs-transaction.charge-point')}
          </Text>
          <Link
            onClick={handleGoToChargePoint}
            disabled={!transaction.chargePoint}>
            {transaction.chargePoint?.name ?? '-'}
          </Link>
        </Stack>
        <Stack
          gap={GapEnum.S}
          justifyContent="space-between"
          alignItems="center">
          <Text bold>
            <Icon name="currency-euro-circle" />
            {t('delmonicos-shared-sdk.outputs-transaction.tariff')}
          </Text>
          <Link onClick={handleGoToTariff} disabled={!transaction.tariffId}>
            {transaction.tariff?.name ?? '-'}
          </Link>
        </Stack>
      </Stack>
    </Card>
  );
};
